import { useEffect, useState } from "react";
import {
  ShieldCheck, ShieldOff, ShieldAlert, Loader2, Copy, Check, CheckCircle2,
  AlertCircle, Download, RefreshCw, KeyRound,
} from "lucide-react";
import { Link } from "react-router-dom";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import LoadError from "@/components/dashboard/LoadError";
import { cn } from "@/lib/cn";
import {
  twofaStatus, twofaSetup, twofaEnable, twofaDisable, twofaRegenerateBackupCodes,
} from "@/api/auth";

// Self-contained 2FA (TOTP) manager — drops into the Settings → Security tab and
// is also rendered standalone by /security/2fa for the mandatory-enrolment gate.
//
// Enrolment is deliberately three steps: confirm the password, scan the secret,
// then prove a code works. The account is only marked as protected at that last
// step — flipping it on when the secret is issued locks out anyone whose scan
// silently failed, because they can't produce a code and can't sign in to fix it.

// The SVG comes from our own backend (segno), but it is still rendered through a
// data: URI in an <img> rather than injected as markup: an <img> can't execute
// script whatever the bytes turn out to be, so the QR needs no trust at all.
function QrImage({ svg, alt }) {
  const src = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  return <img src={src} alt={alt} width={180} height={180} className="rounded-lg bg-white p-2" />;
}

// Shown exactly once, right after enrolment or a regeneration. There is no second
// chance and no "show again" — the server stores only hashes.
function BackupCodes({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const asText = codes.join("\n");

  function copy() {
    navigator.clipboard?.writeText(asText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function download() {
    // Object URL rather than a data: URI — Safari refuses to download the latter
    // from a synthetic click.
    const url = URL.createObjectURL(new Blob([`${asText}\n`], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "two-factor-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4 rounded-xl border border-amber-300 bg-amber-50/60 p-4 dark:border-amber-500/30 dark:bg-amber-500/5">
      <div className="flex items-start gap-2.5">
        <ShieldAlert size={17} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-300" />
        <div className="text-sm text-amber-900 dark:text-amber-100">
          <p className="font-semibold">Save these backup codes now — this is the only time they are shown.</p>
          <p className="mt-1 text-amber-800/90 dark:text-amber-200/90">
            Each one signs you in once if you lose your phone. If you lose both your
            authenticator app <b>and</b> these codes, you can&apos;t get in on your own —
            a company admin has to reset two-factor authentication for your account.
          </p>
        </div>
      </div>

      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-2">
        {codes.map((c) => (
          <li key={c} className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-center font-mono text-sm tracking-wider text-slate-800 dark:border-amber-500/20 dark:bg-white/5 dark:text-slate-100">
            {c}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={copy}>
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy all"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={download}>
          <Download size={15} /> Download
        </Button>
      </div>

      <label className="flex cursor-pointer items-start gap-2.5 border-t border-amber-200 pt-3 text-sm text-amber-900 dark:border-amber-500/20 dark:text-amber-100">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-amber-400 text-brand-600 focus:ring-brand-500/30"
        />
        I have saved these codes somewhere safe.
      </label>

      <Button type="button" size="sm" onClick={onDone} disabled={!acknowledged}>
        Done
      </Button>
    </div>
  );
}

export default function TwoFactorCard({ onEnabled }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);

  // Enrolment: "password" → "scan" → done. null when not enrolling.
  const [step, setStep] = useState(null);
  const [setupData, setSetupData] = useState(null); // {secret, otpauth_uri, qr_svg}
  const [codes, setCodes] = useState(null);         // shown once, then dropped

  // Three separate password fields on purpose: enrolment, regenerate and disable
  // are rendered at the same time once 2FA is on, and one shared value would
  // mirror whatever the user typed into all of them.
  const [pw, setPw] = useState("");
  const [regenPw, setRegenPw] = useState("");
  const [disablePw, setDisablePw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);

  const enabled = !!status?.enabled;

  // A failed status check must never read as "off": swallowing the error showed
  // the Off badge and the "Set up 2FA" button for an account that already has 2FA
  // on — misreporting its security state.
  async function loadStatus() {
    setLoading(true);
    setStatusError(null);
    try {
      setStatus(await twofaStatus());
    } catch (e) {
      setStatusError((e && e.message) || "Couldn't check two-factor status.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetEnrolment() {
    setStep(null);
    setSetupData(null);
    setPw("");
    setCode("");
  }

  async function beginSetup(e) {
    e.preventDefault();
    setBusy(true);
    try {
      setSetupData(await twofaSetup(pw));
      setPw("");
      setStep("scan");
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Couldn't start setup." });
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnable(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await twofaEnable(code);
      resetEnrolment();
      setCodes(res.backup_codes || []);
      await loadStatus();
      // Lets the standalone enrolment page release the mandatory gate the moment
      // the factor exists, instead of stranding the user on a page they've passed.
      if (onEnabled) onEnabled();
      setToast({ type: "success", message: "Two-factor authentication is on." });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Incorrect code." });
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function regenerate(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await twofaRegenerateBackupCodes(regenPw);
      setRegenPw("");
      setCodes(res.backup_codes || []);
      await loadStatus();
      setToast({ type: "success", message: "New backup codes issued — the previous set no longer works." });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Couldn't regenerate backup codes." });
    } finally {
      setBusy(false);
    }
  }

  async function disable(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await twofaDisable(disablePw, code);
      setDisablePw("");
      setCode("");
      await loadStatus();
      setToast({ type: "success", message: "Two-factor authentication disabled." });
    } catch (err) {
      setToast({ type: "error", message: (err && err.message) || "Couldn't disable 2FA." });
    } finally {
      setBusy(false);
    }
  }

  const noPassword = status && status.has_password === false;
  const noEncryption = status && status.encryption_available === false;

  return (
    <div className="card space-y-4 p-6">
      {toast && (
        <div className={cn("flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
          toast.type === "error" ? "border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
                                 : "border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200")}>
          {toast.type === "error" ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />}<span className="flex-1">{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-xs opacity-60">✕</button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
            <ShieldCheck size={17} className="text-brand-500" /> Two-factor authentication
          </h3>
          <p className="mt-0.5 text-sm text-slate-400">
            Add a one-time code from an authenticator app to your sign-in.
          </p>
        </div>
        {loading ? <Loader2 size={16} className="animate-spin text-slate-400" />
          : statusError ? <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">Unknown</Badge>
          : enabled ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">On</Badge>
                    : <Badge className="bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400">Off</Badge>}
      </div>

      <LoadError message={statusError} onRetry={loadStatus} className="mb-0" />

      {/* The one-shot backup-code sheet takes over the card while it's showing —
          nothing else matters until the user confirms they've saved it. */}
      {codes ? (
        <BackupCodes codes={codes} onDone={() => setCodes(null)} />
      ) : (
        <>
          {!loading && !statusError && noEncryption && (
            <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-200">
              <ShieldAlert size={16} className="mt-0.5 shrink-0" />
              <span>
                The server has no <b>CREDENTIALS_FERNET_KEY</b>, so a two-factor secret
                can&apos;t be stored encrypted. Ask an administrator to configure it —
                two-factor authentication is unavailable until then.
              </span>
            </div>
          )}

          {/* An account created purely through Google has no password, so the
              confirm-password step can never succeed. Say so instead of letting
              them hit a confusing "password is incorrect". */}
          {!loading && !statusError && !enabled && noPassword && (
            <div className="flex items-start gap-2.5 rounded-xl border border-sky-200 bg-sky-50/70 px-4 py-3 text-sm text-sky-800 dark:border-sky-500/25 dark:bg-sky-500/10 dark:text-sky-200">
              <KeyRound size={16} className="mt-0.5 shrink-0" />
              <span>
                You sign in with Google and don&apos;t have a password yet. Enabling
                two-factor authentication starts by confirming one, so{" "}
                <Link to="/forgot-password" className="font-semibold underline">set a password first</Link>, then come back here.
              </span>
            </div>
          )}

          {!loading && !statusError && !enabled && !step && !noPassword && !noEncryption && (
            <Button type="button" size="sm" onClick={() => setStep("password")} disabled={busy}>
              <ShieldCheck size={15} /> Set up 2FA
            </Button>
          )}

          {/* Step 1 — re-authenticate before a secret is issued. */}
          {step === "password" && (
            <form onSubmit={beginSetup} className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-white/10">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                First, confirm your password.
              </p>
              <Input type="password" placeholder="Your password" value={pw} autoComplete="current-password"
                onChange={(e) => setPw(e.target.value)} autoFocus />
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={busy || !pw}>
                  {busy ? <Loader2 size={15} className="animate-spin" /> : "Continue"}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={resetEnrolment} disabled={busy}>Cancel</Button>
              </div>
            </form>
          )}

          {/* Steps 2 and 3 — scan, then prove it works. */}
          {step === "scan" && setupData && (
            <form onSubmit={confirmEnable} className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-white/10">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                1. Scan this with your authenticator app (Google Authenticator, Authy, 1Password…):
              </p>
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                {setupData.qr_svg && <QrImage svg={setupData.qr_svg} alt="Two-factor setup QR code" />}
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="text-xs text-slate-400">Can&apos;t scan? Enter this key by hand instead:</p>
                  <div className="flex items-stretch gap-2">
                    <code className="flex-1 break-all rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm tracking-widest dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
                      {setupData.secret}
                    </code>
                    <button type="button"
                      onClick={() => { navigator.clipboard?.writeText(setupData.secret); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-500">
                      {copied ? <Check size={15} /> : <Copy size={15} />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">
                    Account: <span className="font-medium">{setupData.account}</span> · Issuer: {setupData.issuer}
                  </p>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-300">2. Enter the 6-digit code it shows:</p>
              <div className="flex gap-2">
                <Input placeholder="123456" value={code} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                <Button type="submit" size="sm" disabled={busy || code.length !== 6}>
                  {busy ? <Loader2 size={15} className="animate-spin" /> : "Verify & enable"}
                </Button>
              </div>
              <button type="button" onClick={resetEnrolment} className="text-xs text-slate-400 hover:text-slate-600">Cancel setup</button>
            </form>
          )}

          {enabled && !step && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm dark:border-white/10">
                <KeyRound size={15} className="text-slate-400" />
                <span className="text-slate-600 dark:text-slate-300">
                  <b>{status.backup_codes_remaining}</b> of {status.backup_codes_total} backup codes unused.
                </span>
                {status.backup_codes_remaining === 0 && (
                  <span className="text-rose-600 dark:text-rose-300">
                    Generate a new set — you have no way back in if you lose your phone.
                  </span>
                )}
              </div>

              <form onSubmit={regenerate} className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-white/10">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Generate a new set of backup codes. Your current codes stop working immediately.
                </p>
                <Input type="password" placeholder="Your password" value={regenPw} autoComplete="current-password"
                  onChange={(e) => setRegenPw(e.target.value)} />
                <Button type="submit" size="sm" variant="outline" disabled={busy || !regenPw}>
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Regenerate backup codes
                </Button>
              </form>

              <form onSubmit={disable} className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-white/10">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  To turn 2FA off, confirm your password and a current code (a backup code works too).
                </p>
                <Input type="password" placeholder="Your password" value={disablePw} autoComplete="current-password"
                  onChange={(e) => setDisablePw(e.target.value)} />
                <div className="flex gap-2">
                  <Input placeholder="6-digit or backup code" value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())} />
                  <Button type="submit" variant="outline" size="sm" disabled={busy || !disablePw}>
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldOff size={15} />} Disable
                  </Button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
}
